import { useCallback, useEffect, useState } from "react";
import { RefreshCcw, X } from "lucide-react";
import { DotMatrixLoader } from "../icon-bank/DotMatrixLoader";
import { FadingRuler } from "../shared/FadingRuler";
import type { ArbitrumRunPresetId } from "./run-presets";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

interface ArbitrumExpandPanelProps {
  presetIds: ArbitrumRunPresetId[];
  selectedInstrument: string;
  onClose: () => void;
}

interface ArbitrumExpandResponse {
  status: "fresh" | "stale" | "unavailable";
  snapshot: {
    verdictId: string;
    createdAt: string;
    triggerType: string;
    question: string;
    category: string;
    digestText: string;
    confidence: number;
    consensusProbability: number;
    modelCostUsd: number | null;
  } | null;
  expand: {
    headwindOrTailwind: string;
    whoDoesNotKnow: string;
    pricedInRead: string;
    confidenceAdjustment: number;
    adjustedConfidence: number;
    invalidatesIf: string;
  } | null;
  cache: {
    cacheHit: boolean;
    freshnessStatus: "fresh" | "stale" | "miss" | "unavailable";
    matchQuality: "preset" | "scheduled-window" | "latest-window" | "none";
    ageHours: number | null;
    contextHash: string;
    riskSignalWindowDays: number;
    matchedPresetIds: ArbitrumRunPresetId[];
  };
  rerunGate: { label: string };
}

export function ArbitrumExpandPanel({
  presetIds,
  selectedInstrument,
  onClose,
}: ArbitrumExpandPanelProps) {
  const [data, setData] = useState<ArbitrumExpandResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/arbitrum/expand`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          preset_ids: presetIds,
          risk_signal_window_days: 7,
          instrument: selectedInstrument,
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setData((await response.json()) as ArbitrumExpandResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : "expand unavailable");
    } finally {
      setIsLoading(false);
    }
  }, [presetIds, selectedInstrument]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div
      className="fintheon-modal-surface absolute inset-0 z-50 flex flex-col animate-fade-in"
      role="dialog"
      aria-label="Arbitrum Expand"
    >
      <div className="flex shrink-0 items-center justify-between border-b border-[var(--fintheon-accent)]/15 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-[var(--fintheon-accent)]">
            Expand
          </span>
          <StatusPill
            status={data?.status ?? (isLoading ? "fresh" : "unavailable")}
          />
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => void load()}
            disabled={isLoading}
            className="rounded p-1 transition-colors hover:bg-[var(--fintheon-accent)]/10 disabled:opacity-45"
            aria-label="Refresh cached Expand snapshot"
            title="Refresh cached Expand snapshot"
          >
            <RefreshCcw className="h-3.5 w-3.5 text-[var(--fintheon-accent)]/55" />
          </button>
          <button
            onClick={onClose}
            className="rounded p-1 transition-colors hover:bg-[var(--fintheon-accent)]/10"
            aria-label="Close Expand"
          >
            <X className="h-4 w-4 text-[var(--fintheon-text)]/60" />
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {isLoading && !data ? (
          <DotMatrixLoader
            variant="pyramid"
            size={24}
            label="Reading cached snapshot"
          />
        ) : error ? (
          <EmptyState text={`Expand unavailable (${error})`} />
        ) : data?.snapshot && data.expand ? (
          <ExpandRead data={data} />
        ) : (
          <EmptyState
            text={data?.rerunGate.label ?? "No cached snapshot found"}
          />
        )}
      </div>
    </div>
  );
}

function ExpandRead({ data }: { data: ArbitrumExpandResponse }) {
  const read = data.expand;
  if (!read || !data.snapshot) return null;
  const rows = [
    ["Wind", read.headwindOrTailwind],
    ["Blind Spot", read.whoDoesNotKnow],
    ["Priced", read.pricedInRead],
    ["Invalidates", read.invalidatesIf],
  ] as const;
  return (
    <div className="space-y-3">
      <div className="grid gap-2 md:grid-cols-3">
        <Metric
          label="Confidence"
          value={`${read.adjustedConfidence.toFixed(1)}/10`}
        />
        <Metric
          label="Delta"
          value={`${read.confidenceAdjustment >= 0 ? "+" : ""}${read.confidenceAdjustment.toFixed(1)}`}
        />
        <Metric
          label="Cache"
          value={data.cache.matchQuality.replace("-", " ")}
        />
      </div>
      <FadingRuler />
      <p className="text-[11px] leading-relaxed text-[var(--fintheon-text)]/62">
        {data.snapshot.digestText}
      </p>
      <div className="grid gap-2 md:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label} className="rounded-md bg-white/[0.025] px-3 py-2">
            <div className="text-[8.5px] uppercase tracking-[0.14em] text-[var(--fintheon-muted)]/34">
              {label}
            </div>
            <div className="mt-1 text-[10.5px] leading-relaxed text-[var(--fintheon-text)]/64">
              {value}
            </div>
          </div>
        ))}
      </div>
      <div className="text-[8.5px] uppercase tracking-wider text-[var(--fintheon-muted)]/34">
        Snapshot {formatAge(data.snapshot.createdAt)} / {data.cache.contextHash}{" "}
        / {data.rerunGate.label}
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-[var(--fintheon-accent)]/12 px-2.5 py-2">
      <div className="text-[8.5px] uppercase tracking-wider text-[var(--fintheon-muted)]/35">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-[12px] text-[var(--fintheon-accent)]/70">
        {value}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: ArbitrumExpandResponse["status"] }) {
  const label =
    status === "fresh" ? "cached" : status === "stale" ? "stale" : "missing";
  return (
    <span className="rounded border border-[var(--fintheon-accent)]/18 px-1.5 py-0.5 text-[8px] uppercase tracking-wider text-[var(--fintheon-muted)]/45">
      {label}
    </span>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex h-full min-h-[180px] items-center justify-center text-center text-[11px] leading-relaxed text-[var(--fintheon-muted)]/45">
      {text}
    </div>
  );
}

function formatAge(value: string): string {
  const ageHours = Math.max(
    0,
    (Date.now() - new Date(value).getTime()) / 3_600_000,
  );
  if (ageHours < 1) return "just now";
  if (ageHours < 24) return `${Math.round(ageHours)}h ago`;
  return `${Math.floor(ageHours / 24)}d ago`;
}
